/* push.js — Helpers Web Push (notifications gratuites via VAPID).
   Le navigateur s'abonne avec la clé publique VAPID (récupérée du backend),
   puis l'abonnement est envoyé au backend qui pourra pousser des alertes. */
import api from "@/lib/api";

export const pushSupported = () =>
  typeof window !== "undefined" &&
  "serviceWorker" in navigator &&
  "PushManager" in window &&
  "Notification" in window;

function urlBase64ToUint8Array(base64String) {
  const padding = "=".repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, "+").replace(/_/g, "/");
  const raw = window.atob(base64);
  return Uint8Array.from([...raw].map((c) => c.charCodeAt(0)));
}

export async function registerServiceWorker() {
  if (!("serviceWorker" in navigator)) return null;
  try {
    return await navigator.serviceWorker.register("/service-worker.js");
  } catch (e) {
    /* silencieux */
    return null;
  }
}

export async function getPushState() {
  if (!pushSupported()) return { supported: false, subscribed: false, permission: "unsupported" };
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    return { supported: true, subscribed: !!sub, permission: Notification.permission };
  } catch (e) {
    /* silencieux */
    return { supported: true, subscribed: false, permission: Notification.permission };
  }
}

export async function enablePush() {
  if (!pushSupported()) return { ok: false, reason: "unsupported" };
  const permission = await Notification.requestPermission();
  if (permission !== "granted") return { ok: false, reason: permission === "denied" ? "denied" : "dismissed" };

  const { data } = await api.get("/push/vapid-public-key");
  if (!data.enabled || !data.public_key) return { ok: false, reason: "server_disabled" };

  const reg = (await navigator.serviceWorker.getRegistration()) || (await registerServiceWorker());
  if (!reg) return { ok: false, reason: "no_sw" };
  await navigator.serviceWorker.ready;

  let sub = await reg.pushManager.getSubscription();
  if (!sub) {
    sub = await reg.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(data.public_key),
    });
  }
  await api.post("/push/subscribe", sub.toJSON());
  return { ok: true };
}

export async function disablePush() {
  try {
    const reg = await navigator.serviceWorker.getRegistration();
    const sub = reg ? await reg.pushManager.getSubscription() : null;
    if (sub) {
      await api.post("/push/unsubscribe", { endpoint: sub.endpoint });
      await sub.unsubscribe();
    } else {
      await api.post("/push/unsubscribe", {});
    }
    return { ok: true };
  } catch (e) {
    /* silencieux */
    return { ok: false };
  }
}

export async function sendTestPush() {
  try {
    await api.post("/push/test");
    return true;
  } catch (e) {
    /* silencieux */
    return false;
  }
}
